import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlContainer,
    Validators,
} from '@angular/forms';
import { enumMapper } from '@models';
import { UserGroupField } from '@pages/users/modals';
import {
    DirectoryService,
    IDomain,
    SearchContext,
} from '@services';
import { ISuggestValueData } from '@uipath/angular/components/ui-suggest/models';
import { UI_AD_SEARCH_REGEX } from '@validators';

import {
    BehaviorSubject,
    Observable,
    Subject,
} from 'rxjs';
import {
    finalize,
    map,
    takeUntil,
    tap,
} from 'rxjs/operators';

@Component({
    selector: 'ui-ad-search',
    templateUrl: './ad-search.component.html',
    styleUrls: ['./ad-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdSearchComponent implements AfterViewInit, OnDestroy {
    @Input()
    public domainHint = 'user_import_modal_form_domain_name_hint';

    @Input()
    public userOrGroupHint = 'user_import_modal_form_group_name_hint';

    @Output()
    public isLoading = new BehaviorSubject<boolean>(true);

    @Output()
    public selected = new EventEmitter<{ domain: string, username: string }>();

    public restrictInputRegex = UI_AD_SEARCH_REGEX;
    public availableDomains$: Observable<ISuggestValueData<IDomain>[]>;

    public readonly UserGroupField = UserGroupField;
    public readonly searchContext = SearchContext.All;
    public readonly searchContextOptions = enumMapper(SearchContext, {
        resourceKey: 'SearchContext',
    });

    public get form() { return this.controlContainer.control!; }

    private _destroyed$ = new Subject();

    constructor(
        public controlContainer: ControlContainer,
        private _directoryService: DirectoryService,
    ) {
        this.availableDomains$ = this._directoryService
            .getDomains()
            .pipe(
                finalize(() => {
                    this.isLoading.next(false);
                    this.isLoading.complete();
                }),
                map(domains => domains.map(domain => ({
                    id: domain.name,
                    text: domain.name,
                    data: domain,
                }))),
                tap((domains: ISuggestValueData<IDomain>[]) => {
                    const domain = domains.find(d => d.data!.isDefault) || domains[0];
                    this.form.get(UserGroupField.Domain)!.setValue([domain]);
                }),
            );
    }

    ngAfterViewInit() {
        const [domain, username, type] = [
            this.form.get(UserGroupField.Domain)!,
            this.form.get(UserGroupField.UserName)!,
            this.form.get(UserGroupField.Type),
        ];

        username.setValidators(Validators.required);

        domain.valueChanges.pipe(
            takeUntil(this._destroyed$),
        ).subscribe(() => username.setValue([]));

        username.valueChanges.pipe(
            takeUntil(this._destroyed$),
        ).subscribe((value) => {
            if (type) {
                type.setValue(value && value[0] && value[0].type);
            }

            this.selected.emit(
                username.value && username.value[0]
                    ? {
                        domain: domain.value[0].id,
                        username: username.value[0].id,
                    }
                    : undefined,
            );
        });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public adSearchFactory = (searchContext: SearchContext) => (searchTerm: string) => {
        const [domain] = this.form.get(UserGroupField.Domain)!.value;

        return this._directoryService
            .adSearchFactory(domain && domain.id, searchTerm, searchContext);
    }
}
